import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, invId } from "@/lib/invoice";

const REGIONS = [
  { id: "FR", label: "France métropolitaine" },
  { id: "GP", label: "Guadeloupe" },
  { id: "MQ", label: "Martinique" },
  { id: "GF", label: "Guyane" },
  { id: "RE", label: "La Réunion" },
  { id: "YT", label: "Mayotte" },
];

const VAT_RATES = [20, 10, 8.5, 5.5, 2.1, 0];

const emptyLine = () => ({ description: "", qty: 1, unit_price: "", vat_rate: 20 });

const TEMPLATES = [
  {
    id: "btp", label: "BTP", emoji: "🏗️",
    lines: [
      { description: "Fourniture matériaux", qty: 1, unit_price: "", vat_rate: 20 },
      { description: "Main d'œuvre", qty: 1, unit_price: "", vat_rate: 20 },
      { description: "Déplacement / transport", qty: 1, unit_price: "", vat_rate: 20 },
    ],
  },
  {
    id: "resto", label: "Restauration", emoji: "🍽️",
    lines: [
      { description: "Prestation traiteur / repas", qty: 1, unit_price: "", vat_rate: 10 },
      { description: "Boissons", qty: 1, unit_price: "", vat_rate: 20 },
      { description: "Frais de service", qty: 1, unit_price: "", vat_rate: 10 },
    ],
  },
  {
    id: "services", label: "Services", emoji: "💼",
    lines: [
      { description: "Prestation de service", qty: 1, unit_price: "", vat_rate: 20 },
      { description: "Frais annexes", qty: 1, unit_price: "", vat_rate: 20 },
    ],
  },
  {
    id: "consulting", label: "Consulting", emoji: "📊",
    lines: [
      { description: "Journée de consulting", qty: 1, unit_price: "", vat_rate: 20 },
      { description: "Déplacement", qty: 1, unit_price: "", vat_rate: 20 },
    ],
  },
];

const inputCls =
  "w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-sm placeholder:text-zinc-600 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none transition-[border-color,box-shadow] duration-200";

export default function CreateInvoice() {
  const navigate = useNavigate();
  const [buyer, setBuyer] = useState({ name: "", siret: "", email: "", address: "" });
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [region, setRegion] = useState("FR");
  const [lines, setLines] = useState([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const setLine = (i, patch) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const totals = useMemo(() => {
    const ht = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
    const tva = lines.reduce(
      (s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0) * ((Number(l.vat_rate) || 0) / 100),
      0,
    );
    return { ht, tva, ttc: ht + tva };
  }, [lines]);

  const isDom = region !== "FR";

  const submit = async (e) => {
    e.preventDefault();
    if (!buyer.name.trim()) return toast.error("Le nom du client est obligatoire");
    const cleanLines = lines
      .filter((l) => l.description.trim() && Number(l.unit_price) >= 0 && Number(l.qty) > 0)
      .map((l) => ({
        description: l.description.trim(),
        qty: Number(l.qty),
        unit_price: Number(l.unit_price),
        vat_rate: Number(l.vat_rate),
      }));
    if (cleanLines.length === 0) return toast.error("Ajoutez au moins une ligne avec une description et un prix");

    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        direction: "issued",
        source: "manual",
        buyer: {
          name: buyer.name.trim(),
          siret: buyer.siret.trim() || undefined,
          email: buyer.email.trim() || undefined,
          address: buyer.address.trim() || undefined,
        },
        issue_date: issueDate,
        region,
        lines: cleanLines,
        notes: notes.trim() || undefined,
      });
      toast.success("Facture créée — vous pouvez maintenant la convertir en Factur-X");
      const id = invId(data?.invoice || data);
      navigate(id ? `/app/factures/${id}` : "/app/factures");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast.error(typeof detail === "string" ? detail : "Quota atteint — passez à une formule");
        navigate("/app/abonnement");
      } else if (status === 404 || status === 405) {
        toast.error("La création de facture est en cours d'activation côté serveur — réessayez bientôt.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Création impossible — vérifiez les champs");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="create-invoice-page" className="max-w-3xl">
      <Link to="/app/factures" data-testid="create-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200 mb-6">
        <ArrowLeft className="h-4 w-4" /> Retour aux factures
      </Link>

      <p className="overline-label mb-2">Nouvelle facture</p>
      <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl mb-8">Créer de zéro</h1>

      <form onSubmit={submit} className="space-y-6">
        {/* CLIENT */}
        <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="create-buyer-section">
          <p className="overline-label mb-5">Client</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="buyer-name" className="overline-label block mb-2">Nom / Société *</label>
              <input id="buyer-name" data-testid="buyer-name-input" className={inputCls} placeholder="SARL Exemple"
                value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="buyer-siret" className="overline-label block mb-2">SIRET</label>
              <input id="buyer-siret" data-testid="buyer-siret-input" className={inputCls} placeholder="123 456 789 00012"
                value={buyer.siret} onChange={(e) => setBuyer({ ...buyer, siret: e.target.value })} />
            </div>
            <div>
              <label htmlFor="buyer-email" className="overline-label block mb-2">E-mail</label>
              <input id="buyer-email" type="email" data-testid="buyer-email-input" className={inputCls} placeholder="client@exemple.fr"
                value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} />
            </div>
            <div>
              <label htmlFor="buyer-address" className="overline-label block mb-2">Adresse</label>
              <input id="buyer-address" data-testid="buyer-address-input" className={inputCls} placeholder="12 rue des Alizés, 97110 ..."
                value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} />
            </div>
          </div>
        </section>

        {/* INFOS */}
        <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="create-meta-section">
          <p className="overline-label mb-5">Facture</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="issue-date" className="overline-label block mb-2">Date d'émission</label>
              <input id="issue-date" type="date" data-testid="issue-date-input" className={inputCls}
                value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div>
              <label htmlFor="region" className="overline-label block mb-2">Région de vente</label>
              <select id="region" data-testid="region-select" className={inputCls}
                value={region} onChange={(e) => setRegion(e.target.value)}>
                {REGIONS.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          {isDom && (
            <p className="mt-4 text-xs text-[#00D4FF] bg-[#00D4FF]/5 border border-[#00D4FF]/20 rounded-xl px-4 py-3" data-testid="octroi-auto-note">
              Octroi de mer : il sera calculé automatiquement selon les taux de la région sélectionnée et affiché séparément du HT et de la TVA.
            </p>
          )}
        </section>

        {/* LIGNES */}
        <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="create-lines-section">
          <div className="flex items-center justify-between mb-5">
            <p className="overline-label">Lignes de facturation</p>
            <button type="button" onClick={() => setLines((ls) => [...ls, emptyLine()])}
              data-testid="add-line-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#2563EB]/50 text-[#7FA6FF] px-4 py-1.5 text-xs font-semibold hover:bg-[#2563EB]/15 transition-colors duration-200">
              <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="template-chips">
            <span className="text-xs text-zinc-500 mr-1">Modèle :</span>
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setLines(t.lines.map((l) => ({ ...l })));
                  toast.success(`Modèle « ${t.label} » appliqué — ajustez quantités et prix`);
                }}
                data-testid={`template-${t.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3.5 py-1.5 text-xs text-zinc-300 hover:border-[#00D4FF] hover:text-white transition-colors duration-200"
              >
                <span aria-hidden>{t.emoji}</span> {t.label}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`line-row-${i}`}>
                <div className="col-span-12 sm:col-span-5">
                  {i === 0 && <label className="overline-label block mb-2">Description</label>}
                  <input data-testid={`line-description-${i}`} className={inputCls} placeholder="Prestation, produit…"
                    value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  {i === 0 && <label className="overline-label block mb-2">Qté</label>}
                  <input type="number" min="0" step="0.01" data-testid={`line-qty-${i}`} className={inputCls}
                    value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  {i === 0 && <label className="overline-label block mb-2">Prix unit. HT</label>}
                  <input type="number" min="0" step="0.01" data-testid={`line-price-${i}`} className={inputCls} placeholder="0,00"
                    value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  {i === 0 && <label className="overline-label block mb-2">TVA %</label>}
                  <select data-testid={`line-vat-${i}`} className={inputCls}
                    value={l.vat_rate} onChange={(e) => setLine(i, { vat_rate: e.target.value })}>
                    {VAT_RATES.map((r) => (
                      <option key={r} value={r}>{r} %</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
                    disabled={lines.length === 1}
                    data-testid={`remove-line-${i}`}
                    className="rounded-xl border border-white/10 p-3 text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 transition-colors duration-200 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TOTAUX */}
        <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="create-totals-section">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-400">Total HT</span><span className="font-mono" data-testid="total-ht">{eur(totals.ht)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">TVA</span><span className="font-mono" data-testid="total-tva">{eur(totals.tva)}</span></div>
            {isDom && (
              <div className="flex justify-between"><span className="text-[#00D4FF]">Octroi de mer</span><span className="font-mono text-[#00D4FF]" data-testid="total-octroi">calculé à l'enregistrement</span></div>
            )}
            <div className="flex justify-between pt-2 border-t border-white/10"><span className="font-semibold">Total TTC</span><span className="font-mono font-semibold text-lg" data-testid="total-ttc">{eur(totals.ttc)}</span></div>
          </div>
        </section>

        {/* NOTES */}
        <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="create-notes-section">
          <label htmlFor="notes" className="overline-label block mb-2">Notes (facultatif)</label>
          <textarea id="notes" rows={3} data-testid="notes-input" className={inputCls}
            placeholder="Conditions de paiement, références…" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </section>

        <button type="submit" disabled={saving} data-testid="create-invoice-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-4 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer la facture
        </button>
      </form>
    </div>
  );
}
